import { useDotLegalMutation, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { useOptimisticUpdate } from "../../../common/hooks/useOptimisticUpdate";
import { put } from "../../../common/api/apiShared";
import { TemplateEntityType } from "../../../entityTemplates/EntityTemplate.types.ts";
import { EntityTemplatePermissionsProps } from "./EntityTemplatePermissions.tsx";

interface EntityTemplatePermissionItem {
    id: string;
    name: string;
}

interface EntityTemplatePermissionsViewModel {
    excludedTemplateIds: string[];
    allTemplates: EntityTemplatePermissionItem[];
}

interface EntityTemplatePermissionsSaveModel {
    excludedTemplateIds: string[];
}

export function useEntityTemplatePermissions(props: EntityTemplatePermissionsProps) {
    const { roleId, readOnly, templateEntityType } = props;
    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();

    const queryKey = `/roles/${roleId}/entity-template-permissions?templateEntityType=${templateEntityType}`;
    const { isLoading, data } = useDotLegalQueryWithKey<EntityTemplatePermissionsViewModel>(queryKey, queryKey);

    const saveMutation = useDotLegalMutation((model: EntityTemplatePermissionsSaveModel) =>
        put<{}>(`/roles/${roleId}/entity-template-permissions?templateEntityType=${templateEntityType}`, model)
    );

    const templates = data?.allTemplates ?? [];
    const excludedIds = data?.excludedTemplateIds ?? [];

    function isTemplateIncluded(templateId: string) {
        return !excludedIds.includes(templateId);
    }

    async function toggleTemplate(templateId: string) {
        if (readOnly || !data) return;

        const newExcludedIds = isTemplateIncluded(templateId) ? [...excludedIds, templateId] : excludedIds.filter((id) => id !== templateId);

        const updated: EntityTemplatePermissionsViewModel = {
            ...data,
            excludedTemplateIds: newExcludedIds,
        };

        await putOnQueueAndSetQueryData(updated, queryKey, saveMutation.mutateAsync, {
            excludedTemplateIds: newExcludedIds,
        });
    }

    async function onSelectAllChanged() {
        if (readOnly || !data) return;

        const allExcluded = excludedIds.length === 0;
        const newExcludedIds = allExcluded ? templates.map((t) => t.id) : [];

        const updated: EntityTemplatePermissionsViewModel = {
            ...data,
            excludedTemplateIds: newExcludedIds,
        };

        await putOnQueueAndSetQueryData(updated, queryKey, saveMutation.mutateAsync, {
            excludedTemplateIds: newExcludedIds,
        });
    }

    function getHeaderCheckBoxState(): "true" | "false" | "indeterminate" {
        if (templates.length === 0) return "false";
        if (excludedIds.length === 0) return "true";
        if (excludedIds.length === templates.length) return "false";
        return "indeterminate";
    }

    return {
        isLoading,
        templates,
        isTemplateIncluded,
        toggleTemplate,
        onSelectAllChanged,
        headerCheckBoxState: getHeaderCheckBoxState(),
    };
}
